import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import { AWAIT, LOCAL_ACTIONS } from "../agent/agentContract";
import { suggestPrompts } from "../agent/promptSuggestions";

const MAX_LENGTH = 2000;
const DEFAULT_PLACEHOLDER =
  "Ask Copilot to find companies, build prospect lists, or export to your CRM…";

// Contextual ghost text: the agent can send an explicit hint with turn_end;
// otherwise fall back to a generic nudge for the awaited interaction type.
const AWAIT_PLACEHOLDERS = {
  [AWAIT.QUICK_REPLY]: "Pick an option above, or type your answer…",
  [AWAIT.CTA]: "Choose an action above, or tell me what to do next…",
  [AWAIT.CONFIRMATION]: "Confirm above, or type yes / no…",
  [AWAIT.ACTION_RESULT]: "Finish the step above and I'll continue…",
  [AWAIT.QUESTION_FORM]: "Answer the questions above, or just type…"
};

const Composer = observer(() => {
  const [value, setValue] = useState("");
  const [typeaheadIndex, setTypeaheadIndex] = useState(-1);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const textareaRef = useRef(null);
  const wasDisabledRef = useRef(false);
  const routerHistory = useHistory();
  const typeahead = typeaheadOpen
    ? suggestPrompts(value, copilotStore.messages.length > 0)
    : [];

  // Hero prompt rows prefill the box through the store; user edits and sends.
  useEffect(() => {
    if (copilotStore.composerDraft) {
      setValue(copilotStore.composerDraft);
      copilotStore.setComposerDraft("");
      setTypeaheadOpen(false);
      if (textareaRef.current) textareaRef.current.focus();
    }
  }, [copilotStore.composerDraft]);
  // Only a real disconnect disables the box now — an in-flight turn no longer
  // does, so focus never has a reason to leave the composer (like claude.ai:
  // sending while the assistant is responding queues the message instead).
  const disabled = !copilotStore.connected;

  // Auto-grow the textarea up to the SCSS max-height cap.
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [value]);

  // Safety net for the one remaining disable path (disconnect/reconnect):
  // a disabled textarea still gets its focus dropped by the browser, so
  // restore it once the box is enabled again.
  useEffect(() => {
    if (wasDisabledRef.current && !disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  const handleSend = () => {
    const text = value.trim();
    if (!text || disabled) return;
    setValue("");
    setTypeaheadOpen(false);
    setTypeaheadIndex(-1);
    copilotStore.sendUserMessage(text);
  };

  const pickTypeahead = prompt => {
    setValue(prompt);
    setTypeaheadOpen(false);
    setTypeaheadIndex(-1);
    if (textareaRef.current) textareaRef.current.focus();
  };

  const handleKeyDown = event => {
    if (typeahead.length) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setTypeaheadIndex((typeaheadIndex + 1) % typeahead.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setTypeaheadIndex(
          (typeaheadIndex - 1 + typeahead.length) % typeahead.length
        );
        return;
      }
      if (event.key === "Escape") {
        setTypeaheadOpen(false);
        setTypeaheadIndex(-1);
        return;
      }
      if (event.key === "Enter" && !event.shiftKey && typeaheadIndex >= 0) {
        event.preventDefault();
        pickTypeahead(typeahead[typeaheadIndex]);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="copilotComposer">
      {copilotStore.suggestions.length > 0 && !copilotStore.streaming && (
        <div className="copilotComposer__nextActions">
          <div className="copilotComposer__nextActionsTitle">Next steps</div>
          {copilotStore.suggestions.map(suggestion => {
            const isAction = suggestion.kind === "action";
            return (
              <button
                type="button"
                key={`${suggestion.kind}_${suggestion.label}`}
                className={`copilotComposer__nextAction${
                  isAction ? " copilotComposer__nextAction--action" : ""
                }`}
                onClick={() => {
                  // Store handles reply + panel actions; navigation needs the
                  // router history, which only lives here.
                  const handled = copilotStore.applySuggestion(suggestion);
                  if (
                    !handled &&
                    suggestion.actionId === LOCAL_ACTIONS.NAVIGATE &&
                    suggestion.path
                  ) {
                    routerHistory.push(suggestion.path);
                  }
                }}
              >
                <i className="material-icons-outlined">
                  {isAction
                    ? suggestion.icon || "arrow_forward"
                    : "chat_bubble_outline"}
                </i>
                <span className="copilotComposer__nextActionLabel">
                  {suggestion.label}
                </span>
                <i className="material-icons-outlined copilotComposer__nextActionGo">
                  chevron_right
                </i>
              </button>
            );
          })}
        </div>
      )}
      <div className="copilotComposer__inputWrap">
        {typeahead.length > 0 && (
          <div className="copilotComposer__typeahead">
            {typeahead.map((prompt, index) => (
              <button
                type="button"
                key={prompt}
                className={`copilotComposer__typeaheadItem ${
                  index === typeaheadIndex ? "active" : ""
                }`}
                onMouseDown={event => {
                  // mousedown, not click: fires before the textarea blurs
                  event.preventDefault();
                  pickTypeahead(prompt);
                }}
              >
                <i className="material-icons-outlined">search</i>
                <span>{prompt}</span>
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          rows={1}
          placeholder={
            copilotStore.awaitingHint ||
            AWAIT_PLACEHOLDERS[copilotStore.awaiting] ||
            DEFAULT_PLACEHOLDER
          }
          value={value}
          maxLength={MAX_LENGTH}
          onChange={event => {
            setValue(event.target.value);
            setTypeaheadOpen(true);
            setTypeaheadIndex(-1);
          }}
          onBlur={() => setTypeaheadOpen(false)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {copilotStore.streaming ? (
          <button
            type="button"
            className="copilotComposer__sendBtn copilotComposer__sendBtn--stop"
            onClick={copilotStore.interrupt}
            title="Stop"
          >
            <span className="material-icons-round">stop</span>
          </button>
        ) : (
          <button
            type="button"
            className="copilotComposer__sendBtn"
            onClick={handleSend}
            disabled={!value.trim() || disabled}
          >
            <span className="material-icons-round">arrow_upward</span>
          </button>
        )}
      </div>
      <div className="copilotComposer__hint">
        {copilotStore.streaming
          ? "Copilot is responding… click stop to interrupt, or send your next message and it'll queue"
          : "Enter to send · Shift+Enter for a new line"}
      </div>
    </div>
  );
});

export default Composer;
